/*
  Animates the accordion rows of snippets/accordion-row.liquid. Without this script <details> opens and closes
  natively. The panel is clipped only while its height animates, so at rest nothing inside it (focus rings, shadows)
  is cut off. The time and the curve are the theme's motion tokens (motionTokens in global.js), so reduced motion, which
  makes the time 0.01 ms, needs no branch here.

  A dropdown row (accordion-row--dropdown) is a panel under its title from 1,024 px, which assets/component-accordion-row.css
  opens as every panel opens: there the browser opens and closes it, and the script animates nothing.

  A phone-only row is written closed and stays open from 769 px, its title out of the Tab order. The element decides
  that state itself whenever it connects (page load, theme editor reload, Section Rendering) and whenever the window
  crosses 769 px: open and out of the Tab order when wider, back in the Tab order and folded when narrower, except a
  row that shows a message, such as the result of a form just sent. A row written open stays open on a phone until
  the window is resized.
*/

if (!customElements.get('accordion-row')) {
  const wideScreen = windowFrom('large');
  const laptop = windowFrom('laptop');

  class AccordionRow extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.panel = this.querySelector('.accordion-row__panel');
      this.summary = this.details?.querySelector('summary');
      if (!this.summary || !this.panel) return;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.summary.addEventListener('click', (event) => this.toggle(event), { signal });
      this.details.addEventListener('toggle', () => this.reachTables(), { signal });
      this.reachTables();

      if (this.classList.contains('accordion-row--phone-only')) {
        this.fitScreen(true);
        wideScreen.addEventListener('change', () => this.fitScreen(false), { signal });
      }
      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (event) => this.reveal(event.target), { signal });
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.motion?.cancel();
      this.motion = null;
      this.closing = false;
      this.panel?.style.removeProperty('overflow');
    }

    // True while a dropdown row lies under its title as a panel.
    get dropping() {
      return this.classList.contains('accordion-row--dropdown') && laptop.matches;
    }

    // True while a phone-only row is held open on a wide screen.
    get fixedOpen() {
      return this.classList.contains('accordion-row--phone-only') && wideScreen.matches;
    }

    fitScreen(onLoad) {
      this.motion?.cancel();
      this.settle();
      if (wideScreen.matches) {
        this.details.open = true;
        this.summary.tabIndex = -1;
        return;
      }
      this.summary.removeAttribute('tabindex');
      if (onLoad || this.panel.querySelector('.notice:not([hidden])')) return;
      this.details.open = false;
    }

    // The theme editor selected a block: open the row that holds it, or that it holds.
    reveal(target) {
      if (!(target instanceof Node) || this.details.open) return;
      if (target.contains(this) || this.contains(target)) this.details.open = true;
    }

    toggle(event) {
      if (this.dropping) return;
      if (this.fixedOpen) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      const closing = this.details.open && !this.closing;
      // Start from wherever a running animation has got to, so quick double clicks reverse smoothly.
      const from = this.details.open ? this.panel.getBoundingClientRect().height : 0;
      this.motion?.cancel();
      this.closing = closing;
      if (!closing) this.details.open = true;
      const to = closing ? 0 : this.panel.scrollHeight;

      this.panel.style.overflow = 'clip';
      this.motion = this.panel.animate(
        [
          { height: `${from}px`, opacity: closing ? 1 : 0 },
          { height: `${to}px`, opacity: closing ? 0 : 1 },
        ],
        motionTokens()
      );
      // A cancelled animation is always replaced by the next one, which keeps the clip; only a finish lifts it.
      this.motion.onfinish = () => this.settle();
    }

    // A wide table scrolls sideways; while it does, and holds no control of its own, it becomes a tab stop so
    // keyboard users can scroll it with the arrow keys.
    reachTables() {
      if (!this.details.open) return;
      for (const table of this.panel.querySelectorAll('table')) reachScroller(table);
    }

    settle() {
      if (this.closing) this.details.open = false;
      this.closing = false;
      this.motion = null;
      this.panel.style.removeProperty('overflow');
    }
  }

  customElements.define('accordion-row', AccordionRow);
}
